import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { stripeCheckStatus, fetchEntitlement } from "../lib/api";
import { useT } from "../lib/i18n";

export default function PaymentSuccess() {
  const { t, lang } = useT();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [tries, setTries] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;

    const tick = async () => {
      try {
        const s = await stripeCheckStatus(sessionId);
        if (cancelled) return;
        setTries((n) => n + 1);
        if (s.payment_status === "paid") {
          // Confirm entitlement propagated
          try { await fetchEntitlement(); } catch {}
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(s.payment_status)) {
          setStatus(s.payment_status);
          return;
        }
      } catch {}
      if (!cancelled) setTimeout(tick, 1800);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  const enMs = (en, ms) => (lang === "en" ? en : ms);

  return (
    <div className="min-h-screen bg-[#FBFBFD] flex items-center justify-center px-4">
      <div className="bg-white rounded-[1.5rem] p-8 max-w-md w-full shadow-[0_12px_48px_rgba(0,0,0,0.06)] border border-[#E8E8ED] text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 text-[#4FB3E8] animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-[#0A2540]" data-testid="payment-status-polling">
              {enMs("Confirming your payment…", "Mengesahkan pembayaran anda…")}
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              {enMs("Please stay on this page.", "Sila kekal di halaman ini.")}
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-[#0A2540]" data-testid="payment-status-paid">
              {enMs("You're in! Full Access unlocked.", "Berjaya! Full Access telah diaktifkan.")}
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              {enMs("Thanks for supporting TAi-P.", "Terima kasih menyokong TAi-P.")}
            </p>
            <button
              data-testid="continue-btn"
              onClick={() => navigate("/", { replace: true })}
              className="mt-6 chip w-full py-3 rounded-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white font-medium"
            >
              {enMs("Continue", "Teruskan")}
            </button>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <XCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-[#0A2540]" data-testid="payment-status-failed">
              {enMs("Payment couldn't be confirmed", "Pembayaran tidak dapat disahkan")}
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              {enMs("You have not been charged. Please try again from the paywall.", "Anda tidak dicaj. Sila cuba lagi dari halaman paywall.")}
            </p>
            <Link data-testid="back-to-paywall" to="/paywall" className="mt-6 inline-block chip w-full py-3 rounded-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white font-medium">
              {enMs("Back to Paywall", "Kembali ke Paywall")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
